import { useRef, useEffect, useState } from "react"
import Map, { Marker, NavigationControl, Popup } from "react-map-gl/mapbox"
import { useDrop } from "react-dnd"
import { Trash2, DoorOpen, Cpu, Wrench, X } from "lucide-react"

// Map of marker types to icons
const markerIcons = {
  waste: Trash2,
  entry: DoorOpen,
  sensor: Cpu,
  utility: Wrench,
}

// Custom marker component
const CustomMarker = ({ marker, isSelected, onClick, onHover }) => {
  const Icon = markerIcons[marker.type] || Trash2

  return (
    <Marker
      longitude={marker.longitude}
      latitude={marker.latitude}
      anchor="center"
      draggable
      onClick={(e) => {
        e.originalEvent.stopPropagation()
        onClick(marker.id)
      }}
      onMouseEnter={() => onHover(marker.id)}
      onMouseLeave={() => onHover(null)}
      onDragEnd={(e) => marker.onDragEnd(marker.id, e.lngLat.lng, e.lngLat.lat)}
    >
      <div
        className={`flex items-center justify-center w-9 h-9 rounded-full cursor-pointer transition-all ${
          isSelected ? "ring-2 ring-white shadow-lg scale-110" : "ring-1 ring-white/50"
        }`}
        style={{ backgroundColor: marker.color }}
      >
        <Icon size={18} color="white" />
      </div>
    </Marker>
  )
}

function FacilityMap({
  mapboxToken,
  markers,
  viewState,
  onViewStateChange,
  onMarkerClick,
  onMarkerDragEnd,
  onMarkerDelete,
  selectedMarkerId,
  isDrawerOpen,
  onDropOnMap,
  onMapClick,
}) {
  const mapRef = useRef(null)
  const containerRef = useRef(null)
  const [hoveredMarkerId, setHoveredMarkerId] = useState(null)
  const [popupMarkerId, setPopupMarkerId] = useState(null)

  // Set up drop target for the map
  const [{ isOver }, drop] = useDrop(() => ({
    accept: "marker-item",
    drop: (item, monitor) => {
      const clientOffset = monitor.getClientOffset()
      if (!clientOffset || !mapRef.current) return

      // Get the map container's bounds
      const containerBounds = containerRef.current.getBoundingClientRect()

      // Calculate position relative to the map container
      const x = clientOffset.x - containerBounds.left
      const y = clientOffset.y - containerBounds.top

      // Convert corrected screen coordinates to map coordinates
      const map = mapRef.current.getMap()
      const lngLat = map.unproject([x, y])

      onDropOnMap(item, {
        longitude: lngLat.lng,
        latitude: lngLat.lat,
      })
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  }), [onDropOnMap, mapRef]) // Add mapRef as dependency

  // Handle map resize when drawer opens/closes
  useEffect(() => {
    if (!mapRef.current) return
    const timer = setTimeout(() => {
      mapRef.current.resize()
    }, 300)
    return () => clearTimeout(timer)
  }, [isDrawerOpen])

  // Prepare markers with drag end handler
  const markersWithHandlers = markers.map((marker) => ({
    ...marker,
    onDragEnd: onMarkerDragEnd,
  }))

  const hoveredMarker = markers.find((marker) => marker.id === hoveredMarkerId)
  const popupMarker = markers.find((marker) => marker.id === popupMarkerId)

  return (
    <div
      ref={(node) => {
        drop(node)
        containerRef.current = node
      }}
      className={`h-full flex-grow relative ${isOver ? "ring-2 ring-primary ring-inset" : ""}`}
      data-testid="map-drop-area"
    >
      <Map
        ref={mapRef}
        {...viewState}
        onMove={(evt) => onViewStateChange(evt.viewState)}
        onClick={(evt) => {
          onMarkerClick(null) // Deselect marker
          onMapClick(evt) // Add or manage marker
        }}
        style={{ width: "100%", height: "100%" }}
        mapStyle="mapbox://styles/mapbox/streets-v12"
        mapboxAccessToken={mapboxToken}
        attributionControl={true}
        reuseMaps
      >
        <NavigationControl position="top-right" />

        {markersWithHandlers.map((marker) => (
          <CustomMarker
            key={marker.id}
            marker={marker}
            isSelected={marker.id === selectedMarkerId}
            onClick={(id) => {
              setPopupMarkerId(id) // Show detailed popup
              onMarkerClick(id)
            }}
            onHover={setHoveredMarkerId}
          />
        ))}

        {/* Hover Popup */}
        {hoveredMarker && (
          <Popup
            longitude={hoveredMarker.longitude}
            latitude={hoveredMarker.latitude}
            closeButton={false}
            closeOnClick={false}
            offset={[0, -20]}
            className="hover-popup"
          >
            <div className="text-sm font-medium">{hoveredMarker.title}</div>
          </Popup>
        )}

        {/* Detailed Popup */}
        {popupMarker && (
          <Popup
            longitude={popupMarker.longitude}
            latitude={popupMarker.latitude}
            closeButton={false}
            closeOnClick={false}
            offset={[0, -20]}
            className="detailed-popup"
          >
            <div className="flex flex-col gap-2 p-2">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">{popupMarker.title}</h3>
                <button
                  className="text-gray-500 hover:text-gray-800"
                  onClick={() => setPopupMarkerId(null)} // Close popup
                >
                  <X size={16} />
                </button>
              </div>
              <p className="text-sm text-gray-600">{popupMarker.description || "No description provided."}</p>
              <button
                className="text-red-500 hover:text-red-700 text-sm font-medium"
                onClick={() => {
                  onMarkerDelete(popupMarker.id) // Delete marker
                  setPopupMarkerId(null) // Close popup after deletion
                }}
              >
                Delete Marker
              </button>
            </div>
          </Popup>
        )}
      </Map>
    </div>
  )
}

export default FacilityMap
